import { baseApi } from "./baseApi";
import {
  KnowledgeBaseInfo,
  WebsiteList,
  ScrapeNodeScrapedSubUrls,
  ScrapeContentsRequest,
  ScrapeUrlDetailsNode
} from "../../pages/Playground/utils/types";

export const playgroundApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listDocuments: builder.query({
      query: (params = {}) => {
        const {
          skip = 0,
          limit = 5,
          query = "",
          ingestion_type = "",
          filters = null,
          show_custom_documents = false,
        } = params;
        const encodedQuery = encodeURIComponent(query);
        return {
          url: `/playground/list-documents?skip=${skip}&limit=${limit}${
            query ? `&query=${encodedQuery}` : ""
          }${ingestion_type ? `&ingestion_type=${ingestion_type}` : ""}${
            filters
              ? `&sort_by=${filters.sort_by}&sort_order=${filters.sort_order}`
              : ""
          }${
            show_custom_documents !== undefined &&
            `&show_custom_documents=${show_custom_documents}`
          }`,
          method: "GET"
        };
      },
      providesTags: ["Documents"]
    }),
    getPropertiesList: builder.query({
      query: ({
        managerId,
        page,
        pageSize,
        country,
        city,
      }: {
        managerId: string;
        page: number;
        pageSize: number;
        country?: string;
        city?: string;
      }) => {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(pageSize),
        });
        if (country) params.set("country", country);
        if (city) params.set("city", city);
        return {
          url: `/pms/properties/${managerId}?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    getLogs: builder.query({
      query: ({ skip = 0, limit = 8, environment, query, excluded_source , start_date, end_date}) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString()
        });

        if (environment) {
          params.append("environment", environment.toLowerCase());
        }
        if (query) {
          params.append("query", query);
        }
        if (excluded_source) {
          params.append("excluded_source", excluded_source);
        }
        if (start_date && end_date) {
          params.append("start_date", start_date);
          params.append("end_date", end_date);
        }
        return {
          url: `/playground/logs?${params.toString()}`,
          method: "GET"
        };
      },
      providesTags: [{ type: "Logs", id: "LIST" }]
    }),
    checkIfFilesExist: builder.mutation({
      query: (fileNames: string[]) => ({
        url: "/playground/check-if-file-exists",
        method: "POST",
        body: { file_names: fileNames }
      })
    }),
    bulkUploadDocuments: builder.mutation({
      query: ({
        files,
        documentInfo
      }: {
        files: File[];
        documentInfo?: KnowledgeBaseInfo;
      }) => {
        const formData = new FormData();

        if (files.length > 0) {
          files.forEach((file) => {
            formData.append("files", file);
          });
        } else if (documentInfo) {
          formData.append("document_info", JSON.stringify(documentInfo));
        }

        return {
          url: "/playground/upload-bulk-documents",
          method: "POST",
          body: formData,
          formData: true
        };
      }
    }),

    ingestBulkDocuments: builder.mutation({
      query: (
        documentInfo: Array<{
          namespace_id: number | string;
          namespace_name: string;
          file_name: string;
          s3_uri: string;
          campaign_branches: string[];
        }>
      ) => ({
        url: "/playground/ingest-bulk-documents",
        method: "POST",
        body: { document_info: documentInfo }
      }),
      invalidatesTags: ["Documents"]
    }),

    downloadDocument: builder.mutation<
      { preview_url: string; url: string },
      { docId: string; documentType: string; isPreview?: boolean }
    >({
      query: ({ docId, documentType, isPreview }) => ({
        url: `/playground/download-document?document_id=${docId}&document_type=${documentType}&is_preview=${
          isPreview ?? false
        }`,
        method: "GET"
      })
    }),
    deleteLiveDocument: builder.mutation({
      query: (doc_id: string) => ({
        url: `/playground/delete-document?document_id=${doc_id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Documents", "ScrapedNodes", "ScrapedUrls"]
    }),
    deleteBulkDocuments: builder.mutation({
      query: (doc_ids: string[]) => ({
        url: `/playground/delete-documents`,
        method: "DELETE",
        body: { document_ids: doc_ids }
      }),
      invalidatesTags: ["Documents", "ScrapedNodes", "PlaygroundAnalytics"]
    }),
    publishIndividualDoc: builder.mutation({
      query: (docId: string) => ({
        url: `/playground/publish-document?document_id=${docId}`,
        method: "POST"
      }),
      invalidatesTags: ["Documents", "ScrapedNodes"]
    }),
    createCustomQa: builder.mutation({
      query: (body: {
        chunks: Array<{
          question: string;
          answer: string;
          namespace: {
            name: string;
            id: number | string;
          };
        }>;
      }) => ({
        url: "/playground/create-chunk",
        method: "POST",
        body
      }),
      invalidatesTags: ["Documents"]
    }),
    reingestDocument: builder.mutation({
      query: (body: { doc_id: string; s3_uri: string; file_name: string }) => ({
        url: "/playground/reingest-document",
        method: "POST",
        body
      })
    }),
    listScrapedUrls: builder.query<
      { scrape_jobs: WebsiteList[]; count: number },
      { skip?: number; limit?: number; query?: string }
    >({
      query: ({ skip = 0, limit = 5, query = "" }) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString()
        });
        if (query) {
          params.append("query", query);
        }
        return {
          url: `/playground/scrape-urls?${params.toString()}`,
          method: "GET"
        };
      },
      providesTags: ["ScrapedUrls"]
    }),
    scrapeUrl: builder.mutation<
      {
        scrape_job_id?: string;
        error?: string;
        data?: { scrape_job_id?: string };
      },
      { seed_url: string }
    >({
      query: (body) => ({
        url: "/playground/scrape-urls",
        method: "POST",
        body
      }),
      invalidatesTags: ["ScrapedUrls"]
    }),
    getScrapedSubUrls: builder.query<
      { scrape_nodes: ScrapeNodeScrapedSubUrls[]; count: number },
      {
        scrapeJobId: string;
        parentNodeId?: string;
        skip?: number;
        limit?: number;
        query?: string;
      }
    >({
      query: ({
        scrapeJobId,
        parentNodeId,
        skip = 0,
        limit = 10,
        query = ""
      }) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString()
        });
        if (query) {
          params.append("query", query);
        }
        if (parentNodeId) {
          params.append("parent_node_id", parentNodeId);
        }
        return {
          url: `/playground/scrape-nodes/${scrapeJobId}?${params.toString()}`,
          method: "GET"
        };
      },
      providesTags: ["ScrapedNodes"]
    }),
    publishScrapeJob: builder.mutation<void, string>({
      query: (scrapeJobId) => ({
        url: `/playground/publish-scrape-job/${scrapeJobId}`,
        method: "POST"
      }),
      invalidatesTags: ["ScrapedUrls"]
    }),
    scrapeContents: builder.mutation<void, ScrapeContentsRequest>({
      query: (body) => ({
        url: "/playground/scrape-contents",
        method: "POST",
        body
      }),
      invalidatesTags: ["ScrapedUrls", "ScrapedNodes"]
    }),
    getScrapedUrlDetails: builder.query<
      ScrapeUrlDetailsNode[],
      string
    >({
      query: (id) => ({
        url: `/playground/get-scraped-urls/${id}`,
        method: "GET"
      })
    }),
    goLiveAll: builder.mutation({
      query: () => ({
        url: "/playground/publish",
        method: "POST"
      }),
      invalidatesTags: ["Documents", "ScrapedUrls"]
    }),
    getPlaygroundAnalytics: builder.query({
      query: () => ({
        url: "/playground/playground-analytics",
        method: "GET"
      }),
      providesTags: ["PlaygroundAnalytics"]
    }),
    getPlaygroundLeads: builder.query({
      query: (params: {
        startDate: string;
        endDate: string;
        channel: string;
        cursor?: string;
        isFlagged?: boolean;
        isPlaygroundTesting?: boolean;
      }) => ({
        url: `/playground/lead-listing?start_date=${
          params.startDate
        }&end_date=${params.endDate}&channel=${
          params.channel
        }&playground_testing=${params.isPlaygroundTesting ? "true" : "false"}${
          params.cursor ? `&cursor=${params.cursor}` : ""
        }${params.isFlagged ? `&is_flagged=${params.isFlagged}` : ""}`,

        method: "GET"
      })
    }),
    getPlaygroundLeadInboxDetails: builder.query({
      query: (params: {
        id: string;
        leadType: string;
        channel: string;
        isPlaygroundTesting?: boolean;
      }) => ({
        url: `/playground/v2/get-inbox?lead_id=${params.id}&lead_type=${
          params.leadType
        }${
          params.channel ? `&channel=${params.channel}` : ""
        }&playground_testing=${params.isPlaygroundTesting}`,
        method: "GET"
      })
    }),
    playgroundCommunicate: builder.mutation({
      query: (body: {
        query: string[];
        lead_id?: string | null;
        channel: string;
        primary_id?: string | null;
        lead_type?: string | null;
        secondary_id?: string | null;
      }) => ({
        url: "/playground/communicate",
        method: "POST",
        body
      })
    }),
    searchPlaygroundLeads: builder.query({
      query: (body: {
        query: string;
        is_playground_testing?: boolean;
        channel?: string;
      }) => ({
        url: `/dashboard/entity/search?query=${body.query}&playground_testing=${
          body.is_playground_testing
        }${body.channel ? `&channel=${body.channel}` : ""}`,
        method: "GET"
      })
    }),

    getUnansweredQuestions: builder.query({
      query: (query: {
        is_answered: boolean;
        skip: number;
        limit: number;
        sort_order: "asc" | "desc" | "number_of_times_asked" | "created_at";
      }) => {
        let queryString = "";
        if (query.is_answered) {
          queryString += `is_answered=true`;
        } else {
          queryString += `is_answered=false`;
        }
        if (query.skip) {
          queryString += `&skip=${query.skip}`;
        }
        if (query.limit) {
          queryString += `&limit=${query.limit}`;
        }
        if (query.sort_order) {
          if (query.sort_order === "number_of_times_asked") {
            queryString += `&sort_by=${query.sort_order}`;
          } else if (query.sort_order === "asc") {
            queryString += `&sort_order=${query.sort_order}`;
          } else if (query.sort_order === "desc") {
            queryString += `&sort_order=${query.sort_order}`;
          } else if (query.sort_order === "created_at") {
            queryString += `&sort_by=${query.sort_order}`;
          }
        }
        return {
          url: `/playground/get-queries?${queryString}`,
          method: "GET"
        };
      },
      providesTags: ["playground-unanswered-questions"]
    }),

    getUnansweredQueryCount: builder.query({
      query: () => ({
        url: "/playground/get-query-unanswered-count",
        method: "GET"
      }),
      providesTags: ["playground-unanswered-questions"]
    }),

    getUnansweredQueryById: builder.query({
      query: (queryId: string) => ({
        url: `/playground/get-query-unanswered/${queryId}`,
        method: "GET",
      }),
    }),

    answerPlaygroundQuery: builder.mutation({
      query: (body: {
        query_id: string;
        answer: string;
        faq_doc_id: string;
        faq_doc_name: string;
        faq_doc_url: string;
        faq_doc_type: string;
        namespace: {
          name: string;
          id: number;
        };
      }) => ({
        url: `/playground/update-query-unanswered/${body.query_id}`,
        method: "PATCH",
        body: {
          answer: body.answer,
          faq_doc: {
            faq_doc_id: body.faq_doc_id,
            faq_doc_name: body.faq_doc_name,
            faq_doc_url: body.faq_doc_url,
            faq_doc_type: body.faq_doc_type,
            namespace: body.namespace
          }
        }
      }),
      invalidatesTags: ["playground-unanswered-questions"]
    }),
    flagPlaygroundResponse: builder.mutation({
      query: (body: {
        leadId: string;
        messageId: string;
        like?: boolean;
        dislike?: boolean;
        flag?: boolean;
        isPlaygroundTesting?: boolean;
        reason?: string;
        additional_details?: string;
      }) => {
        return {
          url: `/chats/flag_message/${body.leadId}/${body.messageId}`,
          method: "PATCH",
          body: {
            is_playground: body.isPlaygroundTesting,
            reason: body.reason,
            additional_details: body.additional_details
          }
        };
      }
    }),
    unflagPlaygroundResponse: builder.mutation({
      query: (body: { flag_id: string; isPlayground: boolean }) => ({
        url: `/chats/unflag_message/${body.flag_id}`,
        method: "PATCH",
        body: {
          is_playground: body?.isPlayground
        }
      })
    }),
    getFlaggedCounts: builder.query({
      query: (params: { isPlayground?: boolean }) => ({
        url: `/playground/flagged-responses-count?is_playground=${
          params.isPlayground ? "true" : "false"
        }`,
        method: "GET"
      })
    }),
    getPropertiesWithoutDocuments: builder.query({
      query: (client_code: string) => ({
        url: `/playground/alerts/get-properties-without-documents/${client_code}`,
        method: "GET"
      })
    }),
    getWebsitesNotSynced: builder.query({
      query: ({ skip = 0, limit = 20 }: { skip?: number; limit?: number }) => ({
        url: `/playground/alerts/get-websites-not-synced?skip=${skip}&limit=${limit}`,
        method: "GET",
      }),
    }),
    getUseCasesWithBranches: builder.query<
      {
        data: Array<{
          use_case: {
            key: string;
            display_name: string;
          };
          branches: Array<{
            key: string;
            display_name: string;
          }>;
        }>;
      },
      void
    >({
      query: () => ({
        url: "/api/v1/campaigns/usecases-with-branches/list",
        method: "GET"
      })
    }),
    getPendingPublishedDocuments: builder.query({
      query: () => ({
        url: "/playground/pending-documents",
        method: "GET"
      })
    }),
    getLeadListSummary: builder.query({
      query: (params: {
        startDate: string;
        endDate: string;
        channel: string;
        isPlaygroundTesting?: boolean;
        isFlagged?: boolean;
      }) => ({
        url: `/playground/get-summary-from-chat-entities?start_date=${params.startDate}&end_date=${params.endDate}&channel=${params.channel}&playground_testing=${params.isPlaygroundTesting}&is_flagged=${params.isFlagged}`,
        method: "GET"
      })
    }),
    getExsistingChats: builder.query({
      query: ({
        primaryId,
        channel
      }: {
        primaryId: string;
        channel: string;
      }) => {
        const params = new URLSearchParams({
          primary_id: primaryId,
          channel: channel
        });
        return {
          url: `/playground/get-session-inbox-if-exists?${params.toString()}`,
          method: "GET"
        };
      }
    }),
   undoSourceChanges: builder.mutation({
      query: (chunkId: string) => ({
        url: `/playground/discard-draft-chunk?_id=${chunkId}`,
        method: "GET",
      }),
    }),
  }),
  overrideExisting: true
});

export const {
  useListDocumentsQuery,
  useGetPropertiesListQuery,
  useCheckIfFilesExistMutation,
  useLazyListDocumentsQuery,
  useBulkUploadDocumentsMutation,
  useIngestBulkDocumentsMutation,
  useDownloadDocumentMutation,
  useDeleteLiveDocumentMutation,
  useDeleteBulkDocumentsMutation,
  usePublishIndividualDocMutation,
  useCreateCustomQaMutation,
  useReingestDocumentMutation,
  useGetLogsQuery,
  useListScrapedUrlsQuery,
  useLazyListScrapedUrlsQuery,
  useScrapeUrlMutation,
  useGetScrapedSubUrlsQuery,
  useLazyGetScrapedSubUrlsQuery,
  usePublishScrapeJobMutation,
  useScrapeContentsMutation,
  useGetScrapedUrlDetailsQuery,
  useGoLiveAllMutation,
  useGetPlaygroundAnalyticsQuery,
  useLazyGetPlaygroundLeadsQuery,
  useLazyGetPlaygroundLeadInboxDetailsQuery,
  usePlaygroundCommunicateMutation,
  useLazySearchPlaygroundLeadsQuery,
  useGetUnansweredQuestionsQuery,
  useGetUnansweredQueryCountQuery,
  useAnswerPlaygroundQueryMutation,
  useFlagPlaygroundResponseMutation,
  useUnflagPlaygroundResponseMutation,
  useGetFlaggedCountsQuery,
  useGetPropertiesWithoutDocumentsQuery,
  useGetWebsitesNotSyncedQuery,
  useLazyGetWebsitesNotSyncedQuery,
  useGetUseCasesWithBranchesQuery,
  useGetLeadListSummaryQuery,
  useGetPendingPublishedDocumentsQuery,
  useLazyGetUnansweredQueryByIdQuery,
  useLazyGetExsistingChatsQuery,
  useUndoSourceChangesMutation,
} = playgroundApi;
